import { useState, useEffect, useRef } from 'react';
import type { FilterControl } from '../../lib/exploreColumns';
import type { FilterSpec } from '../../types/list';
import { loadTagOptions } from '../../lib/listFacets';

interface Props { control: FilterControl; spec: FilterSpec; onChange: (patch: Partial<FilterSpec>) => void; }

const PILL = 'px-2 py-0.5 rounded-md text-xs border transition-colors';
const ON = 'bg-[#b7ee41] text-[#14310a] border-[#b7ee41]';
const OFF = 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500';

/** Debounced text filter — keeps local state so typing is smooth even while refetches are in flight. */
function TextFilter({
  axis,
  externalValue,
  onChange,
}: {
  axis: keyof FilterSpec;
  externalValue: string;
  onChange: (patch: Partial<FilterSpec>) => void;
}) {
  const [value, setValue] = useState(externalValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the external spec changes (e.g. × clear or top search box resets it)
  useEffect(() => {
    setValue(externalValue);
  }, [externalValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const trimmed = next.trim();
      onChange({ [axis]: trimmed || undefined } as Partial<FilterSpec>);
    }, 300);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <input
      className="input text-xs py-1 w-full"
      placeholder="contains…"
      value={value}
      onChange={handleChange}
    />
  );
}

/** Multi-select enum control — searchable + scrollable so long lists (e.g. 50 states) stay compact. */
function EnumFilter({
  options,
  selected,
  onToggle,
}: {
  options: readonly { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [q, setQ] = useState('');
  const searchable = options.length > 8;
  const shown = q
    ? options.filter(
        (o) => o.label.toLowerCase().includes(q.toLowerCase()) || o.value.toLowerCase().includes(q.toLowerCase()),
      )
    : options;
  return (
    <div className="w-full">
      {searchable && (
        <input
          className="input text-xs py-1 mb-1.5 w-full"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      <div className="flex flex-wrap gap-1 max-h-44 overflow-y-auto">
        {shown.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`${PILL} ${selected.includes(o.value) ? ON : OFF}`}
            onClick={() => onToggle(o.value)}
          >
            {o.label}
          </button>
        ))}
        {shown.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">No matches</span>}
      </div>
    </div>
  );
}

/** Tag filter — fetches the live tag list, multi-selects into tagIds, and (when 2+ chosen)
 *  offers Any/All matching. Tags whose code starts with "src-" are grouped under a
 *  "Sources" subheading; the rest appear under "Tags". Both write to tagIds. */
function TagFilter({ spec, onChange }: { spec: FilterSpec; onChange: (patch: Partial<FilterSpec>) => void }) {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    let active = true;
    loadTagOptions().then((o) => { if (active) setOptions(o); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const cur = (spec.tagIds as string[]) ?? [];
  const toggle = (val: string) => {
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    onChange({ tagIds: next.length ? next : undefined, ...(next.length ? {} : { tagMode: undefined }) });
  };
  const mode = spec.tagMode ?? 'any';

  const sourceOptions = options
    .filter((o) => o.label.startsWith('src-'))
    .map((o) => ({ ...o, label: o.label.slice(4) }));
  const tagOptions = options.filter((o) => !o.label.startsWith('src-'));

  return (
    <div className="w-full space-y-1.5">
      {sourceOptions.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Sources</span>
          <EnumFilter options={sourceOptions} selected={cur} onToggle={toggle} />
        </div>
      )}
      {tagOptions.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Tags</span>
          <EnumFilter options={tagOptions} selected={cur} onToggle={toggle} />
        </div>
      )}
      {options.length === 0 && (
        <span className="text-xs text-gray-400 dark:text-gray-500">No tags yet</span>
      )}
      {cur.length > 1 && (
        <div className="flex gap-1">
          <button type="button" className={`${PILL} ${mode === 'any' ? ON : OFF}`} onClick={() => onChange({ tagMode: undefined })}>Any</button>
          <button type="button" className={`${PILL} ${mode === 'all' ? ON : OFF}`} onClick={() => onChange({ tagMode: 'all' })}>All</button>
        </div>
      )}
    </div>
  );
}

export function ColumnFilterControl({ control, spec, onChange }: Props) {
  if (control.type === 'tags') {
    return <TagFilter spec={spec} onChange={onChange} />;
  }
  if (control.type === 'text') {
    const externalValue = (spec[control.axis] as string) ?? '';
    return <TextFilter axis={control.axis} externalValue={externalValue} onChange={onChange} />;
  }
  if (control.type === 'enum') {
    const cur = (spec[control.axis] as string[]) ?? [];
    const toggle = (val: string) => {
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      onChange({ [control.axis]: next.length ? next : undefined } as Partial<FilterSpec>);
    };
    return <EnumFilter options={control.options} selected={cur} onToggle={toggle} />;
  }
  if (control.type === 'boolean') {
    const v = spec[control.axis] as boolean | undefined;
    const set = (nv: boolean | undefined) => onChange({ [control.axis]: nv } as Partial<FilterSpec>);
    return (
      <div className="flex gap-1">
        <button type="button" className={`${PILL} ${v === undefined ? ON : OFF}`} onClick={() => set(undefined)}>Any</button>
        <button type="button" className={`${PILL} ${v === true ? ON : OFF}`} onClick={() => set(true)}>Yes</button>
        <button type="button" className={`${PILL} ${v === false ? ON : OFF}`} onClick={() => set(false)}>No</button>
      </div>
    );
  }
  if (control.type === 'range') {
    return (
      <div className="flex items-center gap-1">
        <input type="number" className="input text-xs py-1 w-16" placeholder="min" value={(spec[control.minAxis] as number) ?? ''}
          onChange={(e) => onChange({ [control.minAxis]: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<FilterSpec>)} />
        <span className="text-gray-400">–</span>
        <input type="number" className="input text-xs py-1 w-16" placeholder="max" value={(spec[control.maxAxis] as number) ?? ''}
          onChange={(e) => onChange({ [control.maxAxis]: e.target.value === '' ? undefined : Number(e.target.value) } as Partial<FilterSpec>)} />
      </div>
    );
  }
  // dateRange
  return (
    <div className="flex items-center gap-1">
      <input type="date" className="input text-xs py-1" value={(spec[control.fromAxis] as string) ?? ''}
        onChange={(e) => onChange({ [control.fromAxis]: e.target.value || undefined } as Partial<FilterSpec>)} />
      <span className="text-gray-400">–</span>
      <input type="date" className="input text-xs py-1" value={(spec[control.toAxis] as string) ?? ''}
        onChange={(e) => onChange({ [control.toAxis]: e.target.value || undefined } as Partial<FilterSpec>)} />
    </div>
  );
}
